import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import TwoCellRow from "components/TwoCellRow";
import OrderItem from "components/OrderItem";
import PrevButton from "components/PrevButton";
import SplitOrderDetail from "components/SplitOrderDetail";
import { selectPayInWalletBalance, selectBlockedWalletBalance } from "store/user/account/selectors";
import { fetchPayInWalletBalanceSuccess, fetchBlockedWalletBalanceSuccess } from "store/user/account/actions";
import { deactivateOrder, cancelOrder } from "api";
import { useFormattedMessage, useSideModalScrollToTop } from "hooks";
import { orderStatusFancyNames, BUY_ORDER_TYPE, SPLIT_BUY_ORDER_TYPE, ORDER_STATUS_TAGS } from "constants/index";
import { PROJECT_STATUS, ORDER_STATUS } from "constants/status";
import CancelIcon from "assets/images/svg/cancel-icon.svg";
import FailedIcon from "assets/images/svg/failed-icon.svg";
import ProgressIcon from "assets/images/svg/progress-icon.svg";
import NewLoader from "components/NewLoader";

const leftClass = "mb-6 d-block";
const rightClass = "text-14 fw-600 d-block mb-6";

const messages = {
    boEditOverviewTileTitle: "overview",
    totalPurchasePrice: "total_purchase_price",
    editPurchaseOffer: "edit_purchase_offer",
    deactivatePurchaseOffer: "deactivate_purchase_offer",
    deactivateSalesOffer: "deactivate_sales_offer",
    deletePurchaseOffer: "delete_purchase_offer",
    deleteSalesOffer: "delete_sales_offer",
    orderFailedHeading: "order_settlement_failed",
    orderFailedContentP1: "error_processing_order",
    orderFailedContentP2: "shares_balance_not_transferred",
    orderProgressHeading: "purchase_in_progress",
    orderProgressContentP1: "order_prepared_for_processing",
    inform_you_status: "orderProgressContentP2",
    orderCanceledHeading: "purchase_canceled",
    orderCanceledContentP1: "sale_no_longer_carried",
    orderCanceledContentP2: "offer_no_longer_available",
    editSalesOffer: "edit_sales_offer",
    continuePurchaseOffer: "continue_purchase_offer",
    continueSalesOffer: "continue_sales_offer",
    accessBlockedError: "blocked_for_security_reasons",
    tradingPasuedInfo: "trading_pasued_info",
    orderNotAbleToEditMessage: "order_not_able_to_edit",
};

const OrderInfoBox = ({ icon, heading, content }) => {
    return (
        <div className="text-center mt-15 mb-10 mw-400">
            {icon}
            <h4>{heading}</h4>
            {content}
        </div>
    );
};

const OrderDetail = ({
    className = "",
    setCloseTitle,
    stepHeading,
    onEdit,
    onDeactivate,
    onDelete,
    payInAmount,
    blockedAmount,
    fetchPayInWalletBalanceSuccess,
    fetchBlockedWalletBalanceSuccess,
    isOrderItemClicked,
    onBack,
    orderData,
    rowData,
    isRowData = false,
    orderInfoContent,
    isOrderInfoContent = false,
    onContinue,
    isSecondaryMarketUserBlocked,
    projectStatus,
    totalRealizedAmount,
    backSlideEffect = "",
}) => {
    useSideModalScrollToTop();

    const { formatMessage } = useFormattedMessage();

    const [isDeactivatingOrder, setDeactivatingOrder] = useState(false);
    const [isProjectPaused, setIsProjectPaused] = useState(false);
    const [isCancelingOrder, setCancelingOrder] = useState(false);
    const [disableInfo, setDisableInfo] = useState();
    const orderStatusNames = orderStatusFancyNames();

    useEffect(() => {
        setCloseTitle(stepHeading);
    }, []);

    useEffect(() => {
        orderData.status == "";
    }, [orderData]);

    useEffect(() => {
        setDisableInfo();
        setIsProjectPaused(projectStatus == PROJECT_STATUS.MARKETPLACE_PAUSED);
    }, []);

    const editUserOrder = () => {
        onEdit();
    };

    const showDisableInfo = () => {
        setDisableInfo(formatMessage(messages.tradingPasuedInfo));
    };

    const continueUserOrder = () => {
        onContinue();
    };

    const deactivateUserOrder = () => {
        try {
            setDeactivatingOrder(true);

            deactivateOrder(orderData.uid)
                .then((res) => {
                    res && setDeactivatingOrder(false);

                    if (!res || res.errors || res.data.errors || res.data.data.errors) return;

                    onDeactivate(orderData.uid);

                    // remainingShares
                    // if (ALL_SELL_ORDER_STATUS.includes(orderData.tag)) {
                    //     updateUserProjectShares(parseFloat(orderData?.remainingShares) || parseFloat(orderData?.shares))
                    // }

                    // Update payIn wallet balance
                    fetchPayInWalletBalanceSuccess({
                        Amount: parseFloat(payInAmount) + parseFloat(orderData.totalPurchasePrice),
                    });

                    // Update blocked wallet balance
                    fetchBlockedWalletBalanceSuccess({
                        Amount: parseFloat(blockedAmount) - parseFloat(orderData.totalPurchasePrice),
                    });
                })
                .catch(() => {
                    setDeactivatingOrder(false);
                });
        } catch (error) {
            setDeactivatingOrder(false);
        }
    };

    const cancelUserOrder = () => {
        try {
            setCancelingOrder(true);

            cancelOrder(orderData.uid)
                .then((res) => {
                    res && setCancelingOrder(false);

                    if (!res || res.errors || res.data.errors || res.data.data.errors) return;

                    onDelete(orderData.uid);

                    if (orderData?.status === ORDER_STATUS.ACTIVE) {
                        // Update payIn wallet balance
                        fetchPayInWalletBalanceSuccess({
                            Amount: parseFloat(payInAmount) + parseFloat(orderData.totalPurchasePrice),
                        });

                        // Update blocked wallet balance
                        fetchBlockedWalletBalanceSuccess({
                            Amount: parseFloat(blockedAmount) - parseFloat(orderData.totalPurchasePrice),
                        });
                    }
                })
                .catch(() => {
                    setCancelingOrder(false);
                });
        } catch (error) {
            setCancelingOrder(false);
        }
    };

    return (
        <>
            <div className={`buy-order-detail ${className} ${backSlideEffect}`}>
                {/* <h3>{stepHeading}</h3> */}
                <h4>{formatMessage(messages.boEditOverviewTileTitle)}</h4>
                <OrderItem
                    equalTitle={false}
                    tag={orderData?.tag}
                    className={`mt-0 mb-10 ${
                        !ORDER_STATUS_TAGS.includes(orderData?.status)
                            ? BUY_ORDER_TYPE.includes(orderData?.tag) || SPLIT_BUY_ORDER_TYPE.includes(orderData?.tag)
                                ? "border-active-blue"
                                : "border-active-red"
                            : ""
                    }`}
                    image={orderData?.image}
                    title={orderData?.title}
                    // city={orderData?.city}
                    shares={orderData?.shares}
                    remainingShares={orderData?.remainingShares}
                    price={orderData?.price}
                    date={orderData?.status === ORDER_STATUS.SUCCESS ? null : orderData?.date}
                    executionDate={orderData?.status === ORDER_STATUS.SUCCESS ? orderData?.date : null}
                    soldShares={orderData?.soldShares}
                    reservedShares={orderData?.reservedShares}
                    perSharePrice={orderData?.perSharePrice}
                    orderFlag={orderData?.flag}
                    isOrderSplit={orderData?.isOrderSplit}
                    sliptSharePrice={orderData?.sliptSharePrice}
                    totalShare={orderData?.totalShare}
                    orderStatus={orderData?.status}
                />
                {orderData?.isOrderSplit && (
                    <>
                        <SplitOrderDetail
                            soldShares={orderData?.soldShares}
                            reservedShares={orderData?.reservedShares}
                            shares={orderData?.shares}
                        />
                        <div className="border-x" />
                    </>
                )}
                {isRowData && rowData && rowData.length ? (
                    <>
                        <div className="overview-details">
                            {rowData.map((row, index) => {
                                return (
                                    <TwoCellRow
                                        key={index}
                                        removeLClass
                                        removeRClass
                                        lClassname={leftClass}
                                        rClassname={rightClass}
                                        left={row.left}
                                        right={row.right}
                                    />
                                );
                            })}
                        </div>
                        <div className="border-x mt-0" />
                    </>
                ) : null}
                {orderData?.isOrderSplit && isRowData && totalRealizedAmount ? (
                    <>
                        <div className="overview-details">
                            <TwoCellRow
                                removeLClass
                                removeRClass
                                lClassname={leftClass}
                                rClassname={rightClass}
                                left={totalRealizedAmount?.left}
                                right={totalRealizedAmount?.right}
                            />
                        </div>
                        <div className="border-x mt-0" />
                    </>
                ) : null}
                {isRowData && rowData && rowData.length && (
                    <TwoCellRow
                        removeLClass
                        removeRClass
                        lClassname={leftClass}
                        rClassname={rightClass}
                        left="Status"
                        right={(orderData && orderStatusNames[orderData.status]) || ""}
                    />
                )}
                {(isOrderInfoContent || orderInfoContent) && (
                    <OrderInfoBox
                        icon={
                            orderInfoContent?.icon || orderData?.status === ORDER_STATUS.FAILED ? (
                                <FailedIcon />
                            ) : orderData?.status === ORDER_STATUS.CANCELED ? (
                                <CancelIcon />
                            ) : orderData?.status === ORDER_STATUS.PROGRESS ? (
                                <ProgressIcon />
                            ) : null
                        }
                        heading={
                            orderInfoContent?.heading || orderData?.status === ORDER_STATUS.FAILED
                                ? formatMessage(messages.orderFailedHeading)
                                : orderData?.status === ORDER_STATUS.CANCELED
                                ? formatMessage(messages.orderCanceledHeading)
                                : orderData?.status === ORDER_STATUS.PROGRESS
                                ? formatMessage(messages.orderProgressHeading)
                                : null
                        }
                        content={
                            orderInfoContent?.content || orderData?.status === ORDER_STATUS.FAILED ? (
                                <>
                                    <p>{formatMessage(messages.orderFailedContentP1)}</p>
                                    <p>{formatMessage(messages.orderFailedContentP2)}</p>
                                </>
                            ) : orderData?.status === ORDER_STATUS.CANCELED ? (
                                <>
                                    <p>{formatMessage(messages.orderCanceledContentP1)}</p>
                                    <p>{formatMessage(messages.orderCanceledContentP2)}</p>
                                </>
                            ) : orderData?.status === ORDER_STATUS.PROGRESS ? (
                                <>
                                    <p>{formatMessage(messages.orderProgressContentP1)}</p>
                                    <p>{formatMessage(messages.orderProgressContent2)}</p>
                                </>
                            ) : null
                        }
                    />
                )}
                {orderData?.isOrderSplit && (orderData?.soldShares > 0 || orderData?.reservedShares > 0) && onEdit && (
                    <p>{formatMessage(messages.orderNotAbleToEditMessage)}</p>
                )}
                <div className={disableInfo ? "mt-10" : "mt-30"}>
                    {isSecondaryMarketUserBlocked && (onContinue || onEdit || onDeactivate) && (
                        <label className="error mb-4 d-block mw-100">{formatMessage(messages.accessBlockedError)}</label>
                    )}
                    {onContinue ? (
                        <button
                            className={`${
                                !(BUY_ORDER_TYPE.includes(orderData?.tag) || SPLIT_BUY_ORDER_TYPE.includes(orderData?.tag))
                                    ? "btn-sell"
                                    : ""
                            } ${isCancelingOrder || isSecondaryMarketUserBlocked ? "disable-grey" : ""}`}
                            onClick={!(isSecondaryMarketUserBlocked || isCancelingOrder) ? continueUserOrder : null}
                            disabled={isSecondaryMarketUserBlocked || isCancelingOrder}
                        >
                            {BUY_ORDER_TYPE.includes(orderData?.tag) || SPLIT_BUY_ORDER_TYPE.includes(orderData?.tag)
                                ? formatMessage(messages.continuePurchaseOffer)
                                : formatMessage(messages.continueSalesOffer)}
                        </button>
                    ) : null}
                    {!(orderData?.isOrderSplit && (orderData?.soldShares > 0 || orderData?.reservedShares > 0)) && onEdit && (
                        <>
                            {!!disableInfo && (
                                <div className={"mb-15"}>
                                    <span className="text-14 danger-red-color">{disableInfo}</span>
                                </div>
                            )}
                            <button
                                className={`${
                                    isProjectPaused
                                        ? "fade-red-border"
                                        : !(
                                              BUY_ORDER_TYPE.includes(orderData?.tag) ||
                                              SPLIT_BUY_ORDER_TYPE.includes(orderData?.tag)
                                          )
                                        ? "btn-sell"
                                        : ""
                                } ${
                                    isSecondaryMarketUserBlocked || isDeactivatingOrder || isCancelingOrder ? "disable-grey" : ""
                                }`}
                                onClick={
                                    isProjectPaused
                                        ? showDisableInfo
                                        : !(isSecondaryMarketUserBlocked || isDeactivatingOrder || isCancelingOrder)
                                        ? editUserOrder
                                        : null
                                }
                                disabled={isSecondaryMarketUserBlocked || isDeactivatingOrder || isCancelingOrder}
                            >
                                {BUY_ORDER_TYPE.includes(orderData?.tag) || SPLIT_BUY_ORDER_TYPE.includes(orderData?.tag)
                                    ? formatMessage(messages.editPurchaseOffer)
                                    : formatMessage(messages.editSalesOffer)}
                            </button>
                        </>
                    )}
                    {onDeactivate && (
                        <button
                            className={`${
                                isSecondaryMarketUserBlocked || isDeactivatingOrder || isCancelingOrder
                                    ? "disable-grey"
                                    : "secondary"
                            }`}
                            onClick={
                                !(isSecondaryMarketUserBlocked || isDeactivatingOrder || isCancelingOrder)
                                    ? deactivateUserOrder
                                    : null
                            }
                            disabled={isSecondaryMarketUserBlocked || isDeactivatingOrder || isCancelingOrder}
                        >
                            {isDeactivatingOrder ? (
                                <>
                                    <NewLoader />
                                    {BUY_ORDER_TYPE.includes(orderData?.tag) || SPLIT_BUY_ORDER_TYPE.includes(orderData?.tag)
                                        ? formatMessage(messages.deactivatePurchaseOffer)
                                        : formatMessage(messages.deactivateSalesOffer)}
                                </>
                            ) : BUY_ORDER_TYPE.includes(orderData?.tag) || SPLIT_BUY_ORDER_TYPE.includes(orderData?.tag) ? (
                                formatMessage(messages.deactivatePurchaseOffer)
                            ) : (
                                formatMessage(messages.deactivateSalesOffer)
                            )}
                        </button>
                    )}
                    {onDelete && (
                        <button
                            className={`secondary ${isCancelingOrder || isDeactivatingOrder ? "disable-pointer" : ""}`} //previous lighter-grey
                            onClick={!(isCancelingOrder || isDeactivatingOrder) ? cancelUserOrder : null}
                            disabled={isCancelingOrder || isDeactivatingOrder}
                        >
                            {isCancelingOrder ? (
                                <>
                                    <NewLoader />
                                    {BUY_ORDER_TYPE.includes(orderData?.tag) || SPLIT_BUY_ORDER_TYPE.includes(orderData?.tag)
                                        ? formatMessage(messages.deletePurchaseOffer)
                                        : formatMessage(messages.deleteSalesOffer)}
                                </>
                            ) : BUY_ORDER_TYPE.includes(orderData?.tag) || SPLIT_BUY_ORDER_TYPE.includes(orderData?.tag) ? (
                                formatMessage(messages.deletePurchaseOffer)
                            ) : (
                                formatMessage(messages.deleteSalesOffer)
                            )}
                        </button>
                    )}
                </div>
            </div>
            {isOrderItemClicked && (
                <div className="bottom-bar-container">
                    <PrevButton onClick={onBack} />
                </div>
            )}
        </>
    );
};

const mapStateToProps = createStructuredSelector({
    payInAmount: selectPayInWalletBalance,
    blockedAmount: selectBlockedWalletBalance,
});

const mapDispatchToProps = (dispatch) => ({
    fetchPayInWalletBalanceSuccess: (value) => dispatch(fetchPayInWalletBalanceSuccess(value)),
    fetchBlockedWalletBalanceSuccess: (value) => dispatch(fetchBlockedWalletBalanceSuccess(value)),
});

export default connect(mapStateToProps, mapDispatchToProps)(OrderDetail);
